// [목적] 하루 일정 아이템의 시간대 4밴드(morning/noon/afternoon/evening) 판별·그룹화 유틸 + 시각 포맷
// [이유] ADR-FGQ2·ADR-FGQ-OQ1 — FolioDayCardPreview(T11)·PreviewMiniCard(T12)·폴리오 뷰어(T13)가
//         공통으로 사용하는 밴드 그룹핑 로직을 단일 파일로 관리.
//         BE normalizeDayItemRaw 폴백 규칙(scheduledTime 기반 파생)과 동일 로직 유지.
//         folio-generation-quality: buildTimeBand 인자 타입을 TimeBandItem(공용 최소 인터페이스)으로 확장.
//           PlanItem(startAt ISO)과 StreamDayPlanItem(scheduledTime HH:MM) 모두 수용.
//           FolioPage·FolioMobile 의 로컬 groupPlanItemsByBand 헬퍼가 이 함수를 직접 재사용하도록 통일.
//         folio-item-time: formatTimeRange 신규 — startAt/endAt ISO → "HH:MM–HH:MM" 표시 문자열 생성.
// [사이드 이팩트] FolioDayCardPreview.tsx · PreviewMiniCard.tsx · FolioPage.tsx · FolioMobile.tsx 에서 import.
//                 BAND_ORDER 변경 시 모든 소비자의 렌더 순서도 변경됨.
//                 buildTimeBand 시그니처 변경(StreamDayPlanItem → TimeBandItem) — 기존 소비자는 호환(구조적 서브타입).
//                 formatTimeRange 는 TimeBandItem 을 인자로 받아 시각 문자열 또는 null 반환.
// [수정 시 고려사항] 밴드 enum 4종(morning/noon/afternoon/evening)은 streamDayPlanItemSchema.timeOfDay 와 정합 필수.
//                   scheduledTime 파싱은 HH:MM 형식 가정, startAt 파싱은 ISO datetime "T HH:MM" 추출.
//                   두 필드 모두 없거나 파싱 실패 시 'afternoon' 기본값 폴백.
//                   groupItemsByBand 는 StreamDayPlanItem[] 전용으로 유지 — 범용 그룹화는 groupPlanItemsByTimeBand 사용.
//                   startAt/endAt 은 +09:00 KST 오프셋 ISO 문자열("2024-05-20T09:30:00+09:00") 또는 로컬 ISO("2024-05-20T09:30:00").
//                   "T" 이후 HH:MM 추출 방식은 로컬/오프셋 무관하게 동일하게 작동(parseHourFromTimeField 확장).

import type { StreamDayPlanItem } from '@gangwoncanvas/types';

// ── 밴드 타입 ─────────────────────────────────────────────────────────────────

/** 4밴드 시간대 유니온 — streamDayPlanItemSchema.timeOfDay enum 과 정합 */
export type TimeBand = 'morning' | 'noon' | 'afternoon' | 'evening';

// ── 밴드 메타 상수 ────────────────────────────────────────────────────────────

/**
 * [목적] 4밴드별 한국어 라벨·이모지 상수
 * [이유] FolioDayCardPreview 헤더, PreviewMiniCard 칩, 폴리오 뷰어에서 동일 라벨 사용
 * [사이드 이팩트] 없음 (순수 상수)
 * [수정 시 고려사항] 이모지는 색 전달 금지(WCAG §1.4.1) — 항상 텍스트 라벨과 병기
 */
export const BAND_META: Record<TimeBand, { label: string; emoji: string }> = {
  morning: { label: '오전', emoji: '🌅' },
  noon: { label: '점심', emoji: '🍽️' },
  afternoon: { label: '오후', emoji: '☀️' },
  evening: { label: '저녁', emoji: '🌙' },
};

/**
 * [목적] 밴드 렌더 고정 순서 — 빈 밴드 제외 시에도 순서 불변
 * [이유] ADR-FGQ-OQ1: 오전→점심→오후→저녁 순 매거진 하루 서사 표현
 * [사이드 이팩트] groupItemsByBand 반환 순서가 이 배열에 의존
 * [수정 시 고려사항] 순서 변경 시 Spec ADR-FGQ-OQ1 동기 갱신 필요
 */
export const BAND_ORDER: ReadonlyArray<TimeBand> = [
  'morning',
  'noon',
  'afternoon',
  'evening',
] as const;

// ── 공용 최소 인터페이스 ──────────────────────────────────────────────────────

/**
 * [목적] buildTimeBand 가 수용하는 공용 최소 인터페이스
 * [이유] folio-generation-quality: StreamDayPlanItem(scheduledTime HH:MM)과
 *         PlanItem(startAt ISO datetime) 을 동일 함수로 처리하기 위한 구조적 서브타입 계약.
 *         두 타입 모두 이 인터페이스를 만족하므로 타입 단언 없이 buildTimeBand 재사용 가능.
 * [사이드 이팩트] 기존 StreamDayPlanItem 소비자는 구조적 호환 — 변경 불필요.
 * [수정 시 고려사항] PlanItem.startAt 은 ISO datetime("2024-05-20T09:30:00"), scheduledTime 은 HH:MM("09:30").
 *                   timeOfDay 는 양쪽 스키마 모두 nullable().optional() — 부재 시 시각 필드 파생 폴백.
 */
export interface TimeBandItem {
  /** streamDayPlanItemSchema: HH:MM | null. PlanItem 에는 없음(undefined). */
  scheduledTime?: string | null;
  /** PlanItem.startAt: ISO datetime | null. StreamDayPlanItem 에는 없음(undefined). */
  startAt?: string | null;
  /** 양쪽 스키마 공통 — nullable().optional(). 우선순위 1위. */
  timeOfDay?: string | null;
}

// ── scheduledTime / startAt 파싱 헬퍼 ────────────────────────────────────────

/**
 * [목적] "HH:MM" 형식 scheduledTime 또는 ISO datetime startAt 에서 시간(hour) 정수 추출
 * [이유] buildTimeBand 에서 scheduledTime(HH:MM)·startAt(ISO "T HH:MM") 기반 폴백 밴드 파생 시 사용.
 *         folio-generation-quality: startAt ISO 형식 추가 — 뷰어 PlanItem 의 시각 필드 통합 처리.
 * [사이드 이팩트] 없음 (순수 함수)
 * [수정 시 고려사항] HH:MM 직접 매치 또는 ISO "T HH:MM" 추출 두 경로 모두 처리.
 *                   regex 불일치 시 null 반환 — 호출자가 null 분기 처리 필수.
 */
function parseHourFromTimeField(value: string): number | null {
  // ISO datetime: "2024-05-20T09:30:00" 또는 "T09:30" → HH 추출
  const isoMatch = /T(\d{2}):\d{2}/.exec(value);
  if (isoMatch) {
    const hour = parseInt(isoMatch[1]!, 10);
    return isNaN(hour) ? null : hour;
  }
  // HH:MM 직접 형식: "09:30"
  const hmMatch = /^(\d{1,2}):\d{2}$/.exec(value);
  if (hmMatch) {
    const hour = parseInt(hmMatch[1]!, 10);
    return isNaN(hour) ? null : hour;
  }
  return null;
}

/**
 * [목적] ISO datetime 또는 "HH:MM" 문자열에서 { hour, minute } 쌍 추출
 * [이유] folio-item-time: formatTimeRange 가 "HH:MM" 표시 문자열을 생성하기 위해 분(minute)까지 필요.
 *         parseHourFromTimeField 는 hour 만 반환하므로 별도 함수로 분리.
 * [사이드 이팩트] 없음 (순수 함수)
 * [수정 시 고려사항] ISO 오프셋 "+09:00" 포함 문자열("2024-05-20T09:30:00+09:00")도 "T09:30" 추출로 동작.
 *                   KST +09:00 으로 저장된 시각은 "T" 이후 시·분이 그대로 KST 값이므로 Date 변환 불필요.
 *                   파싱 실패 시 null 반환 — 호출자가 null 분기 처리 필수.
 */
function parseHourMinute(value: string): { hour: number; minute: number } | null {
  // ISO datetime: "2024-05-20T09:30:00" 또는 "2024-05-20T09:30:00+09:00" → T HH:MM 추출
  const isoMatch = /T(\d{2}):(\d{2})/.exec(value);
  if (isoMatch) {
    const hour = parseInt(isoMatch[1]!, 10);
    const minute = parseInt(isoMatch[2]!, 10);
    return isNaN(hour) || isNaN(minute) ? null : { hour, minute };
  }
  // HH:MM 직접 형식: "09:30"
  const hmMatch = /^(\d{1,2}):(\d{2})$/.exec(value);
  if (hmMatch) {
    const hour = parseInt(hmMatch[1]!, 10);
    const minute = parseInt(hmMatch[2]!, 10);
    return isNaN(hour) || isNaN(minute) ? null : { hour, minute };
  }
  return null;
}

/**
 * [목적] HH:MM 숫자 쌍을 "HH:MM" 표시 문자열로 변환 (2자리 0-패딩)
 * [이유] folio-item-time: formatTimeRange 내부 공통 포맷 처리 분리.
 * [사이드 이팩트] 없음 (순수 함수)
 * [수정 시 고려사항] 없음
 */
function toHHMM(hour: number, minute: number): string {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

// ── 시각 범위 포맷 ─────────────────────────────────────────────────────────────

/**
 * [목적] 아이템의 startAt/endAt(ISO) 또는 scheduledTime(HH:MM) 에서 표시용 시각 범위 문자열 생성
 * [이유] folio-item-time 요구사항: "10시부터 몇시까지 어디" 처럼 각 일정에 구체 시각을 표시.
 *         기존에는 밴드(오전/점심/오후/저녁)만 표시하고 실제 시각이 미노출.
 *         폴백 우선순위: ① startAt+endAt ISO → "HH:MM–HH:MM",
 *                       ② startAt만 → "HH:MM~",
 *                       ③ scheduledTime(HH:MM) → "HH:MM~",
 *                       ④ 전부 없으면 → null(미표시).
 * [사이드 이팩트] FolioPage.tsx · FolioDayCardPreview.tsx 에서 import 하여 아이템 카드에 시각 라벨 표시.
 *                 null 반환 시 기존 레이아웃 무변경(graceful).
 * [수정 시 고려사항] startAt/endAt 은 +09:00 KST 오프셋 ISO 문자열로 저장 — "T" 이후 HH:MM 추출로 KST 시각 그대로 반영.
 *                   구분자: 범위는 "–"(엔대시 U+2013), 단독 시작은 "~".
 *                   TimeBandItem 인터페이스를 확장하므로 PlanItem·StreamDayPlanItem 모두 수용.
 *                   endAt 필드는 TimeBandItem 에 없으므로 별도 타입 인터페이스(TimeRangeItem)로 수용.
 */
export interface TimeRangeItem extends TimeBandItem {
  /** PlanItem.endAt: ISO datetime | null. endAt 없으면 startAt 단독 표시. */
  endAt?: string | null;
}

/**
 * [목적] TimeRangeItem 에서 "HH:MM–HH:MM" 또는 "HH:MM~" 또는 null 반환
 * [이유] folio-item-time: 시각 표시 로직을 단일 순수 함수로 캡슐화 — 소비자(FolioPage, FolioDayCardPreview)가 null 체크만 하면 됨.
 * [사이드 이팩트] 없음 (순수 함수)
 * [수정 시 고려사항] 폴백 순서: startAt+endAt → startAt 단독 → scheduledTime. 전부 null/undefined 면 null.
 *                   parseHourMinute 실패(잘못된 ISO) 시도 null 처리하여 구버전 데이터 graceful.
 *                   엔대시(–) U+2013 사용 — 하이픈(-)과 구별.
 */
export function formatTimeRange(item: TimeRangeItem): string | null {
  // ① startAt + endAt 모두 있을 때: "HH:MM–HH:MM"
  if (item.startAt) {
    const start = parseHourMinute(item.startAt);
    if (start !== null) {
      if (item.endAt) {
        const end = parseHourMinute(item.endAt);
        if (end !== null) {
          return `${toHHMM(start.hour, start.minute)}–${toHHMM(end.hour, end.minute)}`;
        }
      }
      // ② startAt만 있을 때: "HH:MM~"
      return `${toHHMM(start.hour, start.minute)}~`;
    }
  }

  // ③ scheduledTime(HH:MM) 폴백
  if (item.scheduledTime) {
    const hm = parseHourMinute(item.scheduledTime);
    if (hm !== null) {
      return `${toHHMM(hm.hour, hm.minute)}~`;
    }
  }

  // ④ 전부 없거나 파싱 실패 → null(미표시)
  return null;
}

// ── 밴드 판별 ──────────────────────────────────────────────────────────────────

/**
 * [목적] 아이템의 시간대 밴드를 결정 — item.timeOfDay 우선, 누락 시 시각 필드 파생, 그것도 없으면 'afternoon'
 * [이유] ADR-FGQ2 후방호환 정책: LLM 이 timeOfDay 를 생성하지 않은 구버전 아이템도
 *         scheduledTime / startAt 기반으로 밴드를 표시하여 빈 그룹 방지.
 *         BE normalizeDayItemRaw 폴백 규칙과 동일하게 구현(FE·BE 일관성).
 *         folio-generation-quality: 인자 타입을 TimeBandItem 으로 확장 — PlanItem(startAt)도 수용.
 *         파생 규칙: hour < 11 → morning, 11~13 → noon, 14~17 → afternoon, ≥18 → evening.
 * [사이드 이팩트] FolioDayCardPreview·PreviewMiniCard·FolioPage·FolioMobile 의 밴드 배치에 직접 영향.
 *                 기존 StreamDayPlanItem 소비자는 구조적 호환 — 타입 단언 불필요.
 * [수정 시 고려사항] 밴드 시간 경계(11, 14, 18) 변경 시 BE normalizeDayItemRaw 와 동기 갱신 필수.
 *                   item.timeOfDay 가 enum 범위 외 값이면 폴백 파생으로 진행('afternoon' 최종 기본값).
 *                   scheduledTime 우선, startAt 그다음 순서로 폴백(stream 아이템 우선 설계).
 */
export function buildTimeBand(item: TimeBandItem): TimeBand {
  // 1순위: item.timeOfDay 우선 (LLM 이 밴드를 직접 생성·영속한 경우)
  if (item.timeOfDay != null) {
    const valid: TimeBand[] = ['morning', 'noon', 'afternoon', 'evening'];
    if (valid.includes(item.timeOfDay as TimeBand)) {
      return item.timeOfDay as TimeBand;
    }
  }

  // 2순위: scheduledTime(HH:MM) 기반 파생 — StreamDayPlanItem
  if (item.scheduledTime) {
    const hour = parseHourFromTimeField(item.scheduledTime);
    if (hour !== null) {
      if (hour < 11) return 'morning';
      if (hour < 14) return 'noon';
      if (hour < 18) return 'afternoon';
      return 'evening';
    }
  }

  // 3순위: startAt(ISO datetime) 기반 파생 — PlanItem
  if (item.startAt) {
    const hour = parseHourFromTimeField(item.startAt);
    if (hour !== null) {
      if (hour < 11) return 'morning';
      if (hour < 14) return 'noon';
      if (hour < 18) return 'afternoon';
      return 'evening';
    }
  }

  // 기본값: 오후 (모든 시각 필드 부재·파싱 실패)
  return 'afternoon';
}

// ── 밴드 그룹화 ───────────────────────────────────────────────────────────────

/**
 * [목적] StreamDayPlanItem 배열을 밴드 순서대로 그룹화 — 빈 밴드 제외
 * [이유] FolioDayCardPreview(T11)·PreviewMiniCard(T12)·폴리오 뷰어(T13)가 동일 그룹 구조 사용.
 *         BAND_ORDER 고정 순서를 유지하되 비어 있는 밴드는 렌더에서 제외.
 * [사이드 이팩트] items 순서가 각 밴드 내에서는 원본 순서 유지(안정 정렬)
 * [수정 시 고려사항] 반환값의 band 는 BAND_ORDER 순서 보장 — 소비자는 순서를 믿고 렌더.
 *                   범용 그룹화(PlanItem 포함)는 groupPlanItemsByTimeBand<T> 사용.
 */
export function groupItemsByBand(
  items: StreamDayPlanItem[],
): Array<{ band: TimeBand; items: StreamDayPlanItem[] }> {
  // 밴드별 버킷 초기화
  const buckets = new Map<TimeBand, StreamDayPlanItem[]>(BAND_ORDER.map((band) => [band, []]));

  // 아이템을 각 밴드 버킷에 배치
  for (const item of items) {
    const band = buildTimeBand(item);
    buckets.get(band)!.push(item);
  }

  // BAND_ORDER 순서로 정렬하고 빈 밴드 제외
  return BAND_ORDER.filter((band) => buckets.get(band)!.length > 0).map((band) => ({
    band,
    items: buckets.get(band)!,
  }));
}

/**
 * [목적] TimeBandItem 을 만족하는 임의 타입 T 의 배열을 밴드 순서대로 그룹화 — 빈 밴드 제외
 * [이유] folio-generation-quality: FolioPage·FolioMobile 의 로컬 groupPlanItemsByBand 헬퍼를
 *         이 범용 함수로 대체하여 로직 중복 제거.
 *         PlanItem(startAt) 과 StreamDayPlanItem(scheduledTime) 모두 TimeBandItem 을 만족하므로
 *         동일 함수로 처리 가능. buildTimeBand 의 3단계 폴백(timeOfDay → scheduledTime → startAt)을 재사용.
 * [사이드 이팩트] FolioPage.tsx·FolioMobile.tsx 의 로컬 그룹핑 헬퍼가 이 함수로 교체됨.
 *                 items 순서가 각 밴드 내에서는 원본 순서 유지(안정 정렬).
 * [수정 시 고려사항] T 는 TimeBandItem 의 구조적 서브타입이면 충분 — 추가 필드 허용.
 *                   반환값의 band 는 BAND_ORDER 순서 보장.
 *                   빈 밴드는 반환 배열에서 제외 — 소비자는 항상 length > 0 가정 가능.
 */
export function groupPlanItemsByTimeBand<T extends TimeBandItem>(
  items: T[],
): Array<{ band: TimeBand; items: T[] }> {
  const buckets = new Map<TimeBand, T[]>(BAND_ORDER.map((band) => [band, []]));

  for (const item of items) {
    const band = buildTimeBand(item);
    buckets.get(band)!.push(item);
  }

  return BAND_ORDER.filter((band) => buckets.get(band)!.length > 0).map((band) => ({
    band,
    items: buckets.get(band)!,
  }));
}

/**
 * [목적] 예보 날짜 'YYYYMMDD' → "9월 21일(일)" 표시 문자열
 * [이유] [2026-09-21 사용자 요청] 기존에는 여행 날짜 미확정 폴리오에 '오늘 기준' 배지만 붙였는데,
 *        "며칠 예보인지"가 바로 읽히지 않았다. 실제 월·일과 요일을 보여주는 편이 직관적이다.
 * [사이드 이팩트] 없음(순수 함수). FolioPage / FolioMobile 의 WeatherLabel 이 공유한다.
 * [수정 시 고려사항] 입력은 KST 기준 달력 날짜다 — Date 로 파싱할 때 UTC 로 만들고 UTC 게터로 읽어야
 *                   로컬 타임존이 UTC 인 서버·CI 에서 하루가 밀리지 않는다.
 */
export function formatForecastDate(ymd: string | null | undefined): string | null {
  if (!ymd || !/^\d{8}$/.test(ymd)) return null;
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(4, 6));
  const d = Number(ymd.slice(6, 8));
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return null;
  const dow = ['일', '월', '화', '수', '목', '금', '토'][dt.getUTCDay()] ?? '';
  return `${m}월 ${d}일(${dow})`;
}
